import React from "react";

export function PageHeader({ title, subtitle, action, testid }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8 animate-fade-in" data-testid={testid || "page-header"}>
      <div>
        <h1 className="font-heading font-semibold text-2xl lg:text-3xl tracking-tight leading-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 text-[13.5px] max-w-2xl">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function EmptyState({ title, subtitle, action, icon: Icon }) {
  return (
    <div className="surface-card p-10 text-center" data-testid="empty-state">
      {Icon && (
        <div className="mx-auto mb-4 size-12 flex items-center justify-center rounded-xl bg-[hsl(var(--gold)/0.1)] gold-border border">
          <Icon className="size-6 gold-text" strokeWidth={1.4}/>
        </div>
      )}
      <h3 className="font-heading font-semibold text-base mb-1">{title}</h3>
      {subtitle && <p className="text-muted-foreground text-[13px] mb-4">{subtitle}</p>}
      {action}
    </div>
  );
}

export function StatusBadge({ tone = "neutral", children, testid }) {
  const tones = {
    strong: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    neutral: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    risky: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    hostile: "bg-red-500/15 text-red-400 border-red-500/30",
    valid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    contested: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    inadmissible: "bg-red-500/15 text-red-400 border-red-500/30",
    defense: "bg-[hsl(var(--gold)/0.15)] gold-text border-[hsl(var(--gold)/0.35)]",
    prosecution: "bg-red-500/12 text-red-400 border-red-500/30",
    high: "bg-red-500/15 text-red-400 border-red-500/30",
    critical: "bg-red-500/20 text-red-300 border-red-500/40",
    medium: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    todo: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    in_progress: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    done: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    gold: "bg-[hsl(var(--gold)/0.15)] gold-text border-[hsl(var(--gold)/0.4)]",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-wider border ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

export function Gauge({ value = 0, label, testid, tone = "gold" }) {
  const safe = Math.max(0, Math.min(100, value));
  const toneColor = tone === "gold" ? "hsl(var(--gold))" :
                    tone === "risk" ? (safe > 65 ? "#EF4444" : safe > 35 ? "#F59E0B" : "#10B981") :
                    "hsl(var(--gold))";
  return (
    <div className="flex flex-col" data-testid={testid}>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className="stat-num text-2xl" style={{ color: toneColor }}>{safe}</span>
        <span className="text-muted-foreground text-[11px] font-mono">/100</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${safe}%`, background: toneColor }}
        />
      </div>
      {label && <div className="text-[11px] text-muted-foreground mt-2 uppercase tracking-wider font-mono">{label}</div>}
    </div>
  );
}
